import React from 'react';
import { useLocation } from 'react-router-dom';
import jsonp from 'jsonp';
import * as Sentry from '@sentry/browser';
import * as FormNames from './formNames';
import { SlowFormUrl, TrackValuesToPrimaryTrack } from './constants';
import { CallbackSuffix } from './formNames';
import { getFormData } from './sessionStorage';
import { getData as localGetData, storeData as localStoreData } from './localStorage';
import { trackFormSubmission } from './monitoring';

const serializePardotData = (data) => {
  if (data.primary_track) {
    data.prospect_form_role_type = data.primary_track;
    data.primary_track = TrackValuesToPrimaryTrack[data.primary_track];
  }

  return data;
};

const arrayToSet = (arr) => {
  const set = {};
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i];
    set[item] = true;
  }
  return set;
};

const objectToUrlParams = (data) => {
  const params = [];
  Object.keys(data).forEach((key) => {
    if (data && Object.prototype.hasOwnProperty.call(data, key)) {
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`);
    }
  });
  return `?${params.join('&')}`;
};

const attachFormValue = (element, field, value) => {
  const node = document.createElement('input');
  node.type = 'hidden';
  node.name = field;
  node.value = value;
  element.appendChild(node);
};

const jsonpTry = (url, options, callback) => {
  jsonp(url, options, (err, res) => {
    if (err) {
      jsonp(url, options, callback);
    } else {
      callback(err, res);
    }
  });
};

// formName is used to create a unique jsonp callback name
// callbackLocations is required for JSONP submissions to Pardot
//   Each property points to a unique .js file for each formName's
//   success and error handler containing the correct jsonp
//   callback name.
//   - success_location : success .js file
//   - error_location   : error .js file
// timeout set to 12 seconds
// callback takes args of (error, response)
const pardotFormSubmission = (formName, url, callbackLocations, data, callback) => {
  if (!data || typeof data !== 'object') {
    Sentry.captureMessage(`Form submission missing data. Page: ${formName}`);
    return;
  }
  if (!callbackLocations || typeof callbackLocations !== 'object') {
    Sentry.captureMessage(`Form submission missing callbackLocations. Page: ${formName}`);
    return;
  }
  const formData = objectToUrlParams({ ...data, ...callbackLocations });
  const callbackName = `${formName}${CallbackSuffix}`;
  data.jsonp_callback = callbackName;
  const requestUrl = `${url}${formData}`;
  // Allows logging of requestUrl to Sentry
  const wrappedCallback = (err, res) => {
    const response = res || {};
    response.requestUrl = requestUrl;
    callback(err, response);
  };
  jsonpTry(requestUrl, { name: callbackName, timeout: 12000 }, wrappedCallback);
};

const submitAllDataToSlowForm = (page, pageName, submitText) => {
  let allCachedData = {};
  for (const [, formKey] of Object.entries(FormNames)) {
    const cachedData = getFormData(formKey);
    allCachedData = { ...allCachedData, ...cachedData };
  }
  allCachedData = serializePardotData(allCachedData);
  if (page) {
    trackFormSubmission({
      data: allCachedData,
      pageName,
      pageNumber: page,
      submitText,
      message: 'Enrollment Portal SlowForm Fallback',
    });
  }
  const resubmitForm = document.createElement('form');
  resubmitForm.method = 'GET';
  resubmitForm.action = SlowFormUrl;
  for (const [field, value] of Object.entries(allCachedData)) {
    attachFormValue(resubmitForm, field, value);
  }
  document.body.appendChild(resubmitForm);
  window.setTimeout(() => {
    resubmitForm.submit();
  }, 500);
};

// Only write given paramets: don't overwrite existing with null.
const storeUTMParams = (rawURL) => {
  const url = new URL(rawURL);
  if (url.searchParams.get('utm_campaign')) {
    localStoreData('utmCampaign', url.searchParams.get('utm_campaign'));
  }
  if (url.searchParams.get('utm_medium')) {
    localStoreData('utmMedium', url.searchParams.get('utm_medium'));
  }
  if (url.searchParams.get('utm_source')) {
    localStoreData('utmSource', url.searchParams.get('utm_source'));
  }
  if (document.referrer) {
    localStoreData('referrerUrl', document.referrer);
  } else if (localGetData('referrerPathrise')) {
    localStoreData('referrerUrl', localGetData('referrerPathrise'));
  }
  if (url.searchParams.get('utm_content')) {
    localStoreData('utmContent', url.searchParams.get('utm_content'));
  }
  if (url.searchParams.get('utm_term')) {
    localStoreData('utmTerm', url.searchParams.get('utm_term'));
  }
  if (url.searchParams.get('gclid')) {
    localStoreData('gclid', url.searchParams.get('gclid'));
  }
};

// Don't send "null" as a value
const fetchLocalStorageParams = () => {
  const data = {};
  if (localGetData('pathriseFirstUrlVisited')) {
    data.first_url_visited = localGetData('pathriseFirstUrlVisited');
  }
  if (localGetData('utmCampaign')) {
    data.utm_campaign = localGetData('utmCampaign');
  }
  if (localGetData('utmMedium')) {
    data.utm_medium = localGetData('utmMedium');
  }
  if (localGetData('utmSource')) {
    data.utm_source = localGetData('utmSource');
  }
  if (localGetData('utmContent')) {
    data.utm_content = localGetData('utmContent');
  }
  const gclid = localGetData('gclid');
  if (gclid) {
    data.gclid = gclid.value ?? gclid;
  }
  if (localGetData('referrerUrl')) {
    data.referrer_url = localGetData('referrerUrl');
  }
  return data;
};

const fileMIMEType = (fileName) => {
  switch (true) {
    case /\.pdf$/.test(fileName):
      return 'application/pdf';
    case /\.doc$/.test(fileName):
      return 'application/msword';
    case /\.docx$/.test(fileName):
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    default:
      return 'application/octet-stream';
  }
};

function pseudoGUID(length) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

function preloadImage(imgUrl) {
  const image = new Image();
  image.src = imgUrl;
}

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export {
  arrayToSet,
  attachFormValue,
  fetchLocalStorageParams,
  fileMIMEType,
  objectToUrlParams,
  pardotFormSubmission,
  preloadImage,
  pseudoGUID,
  serializePardotData,
  storeUTMParams,
  submitAllDataToSlowForm,
  useQuery,
};
